import { useProfile } from '@hooks/getQueries';
import { useUpdateJoyrideConfig } from '@hooks/onboardingQueries';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { getDefaultSteps } from '../utils/onBoardignSteps';

const useJoyrideTour = (customSteps = [], onTourComplete) => {
  const { mutateAsync: completeAreaOnboarding } = useUpdateJoyrideConfig();
  const { data: userData, refetch, isPending } = useProfile();
  const [isJoyrideOpen, setIsJoyrideOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [completionDialog, setCompletionDialogOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const tourArea = pathname === '/' || pathname.endsWith('dashboard') ? 'dashboard' : 'dashboard';
  const [searchParams] = useSearchParams();
  useEffect(() => {
    try {
      localStorage.setItem('joyride_active_dashboard', isJoyrideOpen ? 'true' : 'false');
    } catch {
      // Ignore storage errors
    }
  }, [isJoyrideOpen]);

  useEffect(() => {
    const timestamp = searchParams.get('ts');
    const isValidTimestamp = timestamp && Date.now() - Number(timestamp) < 300000; // 5 minutes
    const transactionId = searchParams.get('transactionId');
    if (
      isPending ||
      (searchParams.get('payment') === 'success' && isValidTimestamp && transactionId)
    ) {
      setIsJoyrideOpen(false);
      return;
    }
    const isCompleted = userData?.joyride_metadata?.data?.dashboard?.completed;

    setIsJoyrideOpen(!isCompleted);
  }, [userData, isPending, pathname, searchParams]);

  const restartTour = async () => {
    await completeAreaOnboarding(
      {
        step: '0',
        data: {
          aiParticipants: userData?.joyride_metadata?.data?.aiParticipants || {},

          dashboard: {
            area: tourArea,
            pathname: pathname,
            completed: false,

            timestamp: new Date().toISOString(),
            completionMethod: 'restart',
          },
        },
      },
      {
        onSuccess: () => {
          refetch();
        },
        onError: (error) => {
          console.error('Tour restart failed:', error);
        },
      },
    );
  };

  const steps = customSteps.length > 0 ? customSteps : getDefaultSteps(setIsJoyrideOpen, navigate);

  const handleJoyrideCallback = useCallback(
    (data) => {
      const { status, index, action } = data;

      if (status === 'finished' || status === 'skipped') {
        setIsJoyrideOpen(false);
        // localStorage.setItem('hasCompletedTour', 'true');
        setCompletionDialogOpen(true);
        // Call the onTourComplete callback to show the free trial popup

        // Call the onTourComplete callback to show the free trial popup
        completeAreaOnboarding(
          {
            step: String(index),
            data: {
              dashboard: {
                area: tourArea,
                pathname: pathname,
                completed: true,

                timestamp: new Date().toISOString(),
                completionMethod: status,
              },
              aiParticipants: userData?.joyride_metadata?.data?.aiParticipants || {},
            },
          },
          {
            onSuccess: () => {
              refetch();
            },
          },
        );
      }

      setActiveStep(index);
      if (action === 'next' && index === 6) {
        const profileButton = document.querySelector('[data-tour="profile-menu-button"]');
        profileButton?.click();
      }
    },
    [steps, pathname, tourArea, completeAreaOnboarding],
  );

  return {
    steps,
    isJoyrideOpen,
    activeStep,
    handleJoyrideCallback,
    setIsJoyrideOpen,
    restartTour,
    CompletionDialog: (
      <Dialog open={completionDialog} onClose={() => setCompletionDialogOpen(false)}>
        <DialogTitle>Tour Completed</DialogTitle>
        <DialogContent>
          <Typography>
            You have completed the tour! You can always restart it from the profile menu.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCompletionDialogOpen(false);
              if (onTourComplete && typeof onTourComplete === 'function') {
                onTourComplete();
              }
            }}
            color="primary"
          >
            Got it!
          </Button>
        </DialogActions>
      </Dialog>
    ),
  };
};

export default useJoyrideTour;
